import api, { API_BASE } from './api';
import {
  CweEntry,
  FileEntity,
  Risikoszenario,
  SandboxFile,
  SandboxFinding,
  SandboxFindingListItem,
  SandboxFindingPayload,
  SandboxFreigabe,
  Schwachstellenklasse,
  SharedAuditDetail,
  SharedAuditListItem,
  SharedFinding,
  SharedFindingListItem,
  Tool,
} from '../types';

/**
 * Client fuer /api/restricted (isolierte Umgebung).
 *
 * Wird sowohl von der eingeschraenkten Sitzung (Restricted-Login) als auch vom
 * Hauptaccount (Seite "Isolierte Umgebung") verwendet. Endpoints, die nur der
 * Hauptaccount nutzen darf (restore/import/delete/freigaben), liefern fuer die
 * eingeschraenkte Sitzung 403.
 */

/** Minimale Datei-API mit derselben Signatur wie `filesService` (fuer ScreenshotGallery / TipTapEditor). */
export interface FileApi {
  list: (params?: { schwachstelle_id?: number }) => Promise<FileEntity[]>;
  upload: (file: File, typ?: string, projekt_id?: number, audit_id?: number, schwachstelle_id?: number) => Promise<FileEntity>;
  download: (id: number) => void;
  getPreviewUrl: (id: number) => string;
  delete: (id: number) => Promise<void>;
  /** Bild-Annotationen: bearbeitete Version anlegen bzw. ueberschreiben. */
  createBearbeitung?: (originalId: number, rendered: Blob, annotationen: string) => Promise<FileEntity>;
  updateBearbeitung?: (fileId: number, rendered: Blob, annotationen: string) => Promise<FileEntity>;
}

const toFileEntity = (f: SandboxFile, schwachstelleId?: number): FileEntity => ({
  id: f.id,
  typ: f.typ ?? undefined,
  dateiname: f.dateiname,
  dateigroesse: f.dateigroesse ?? undefined,
  dateipfad: '',
  erstellt_am: f.erstellt_am ?? undefined,
  schwachstelle_id: schwachstelleId,
  original_file_id: f.original_file_id ?? null,
  annotationen: f.annotationen ?? null,
});

const bearbeitungForm = (rendered: Blob, annotationen: string): FormData => {
  const formData = new FormData();
  formData.append('file', rendered, 'bearbeitet.png');
  formData.append('annotationen', annotationen);
  return formData;
};

const sandboxFiles = {
  list: async (schwachstelleId: number): Promise<SandboxFile[]> =>
    (await api.get(`/restricted/findings/${schwachstelleId}/files`)).data,
  upload: async (schwachstelleId: number, file: File, typ?: string): Promise<SandboxFile> => {
    const formData = new FormData();
    formData.append('file', file);
    if (typ) formData.append('typ', typ);
    return (await api.post(`/restricted/findings/${schwachstelleId}/files`, formData)).data;
  },
  delete: async (fileId: number): Promise<void> => {
    await api.delete(`/restricted/files/${fileId}`);
  },
  get: async (fileId: number): Promise<SandboxFile> => (await api.get(`/restricted/files/${fileId}`)).data,
  createBearbeitung: async (originalId: number, rendered: Blob, annotationen: string): Promise<SandboxFile> =>
    (
      await api.post(`/restricted/files/${originalId}/bearbeitungen`, bearbeitungForm(rendered, annotationen), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data,
  updateBearbeitung: async (fileId: number, rendered: Blob, annotationen: string): Promise<SandboxFile> =>
    (
      await api.put(`/restricted/files/${fileId}/bearbeitungen`, bearbeitungForm(rendered, annotationen), {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
    ).data,
  previewUrl: (fileId: number) => `${API_BASE}/restricted/files/${fileId}/preview`,
  downloadUrl: (fileId: number) => `${API_BASE}/restricted/files/${fileId}/download`,
};

/** FileApi-Adapter fuer ScreenshotGallery / TipTapEditor (eigene Sandbox-Findings). */
const sandboxFileApi: FileApi = {
  list: async (params?: { schwachstelle_id?: number }): Promise<FileEntity[]> => {
    if (!params?.schwachstelle_id) return [];
    const rows = await sandboxFiles.list(params.schwachstelle_id);
    return rows.map((f) => toFileEntity(f, params.schwachstelle_id));
  },
  upload: async (file: File, typ?: string, _p?: number, _a?: number, schwachstelle_id?: number): Promise<FileEntity> => {
    if (!schwachstelle_id) throw new Error('Finding muss zuerst gespeichert werden');
    const created = await sandboxFiles.upload(schwachstelle_id, file, typ);
    return toFileEntity(created, schwachstelle_id);
  },
  download: (id: number) => {
    window.open(sandboxFiles.downloadUrl(id), '_blank');
  },
  getPreviewUrl: (id: number) => sandboxFiles.previewUrl(id),
  delete: async (id: number) => sandboxFiles.delete(id),
  createBearbeitung: async (originalId: number, rendered: Blob, annotationen: string): Promise<FileEntity> =>
    toFileEntity(await sandboxFiles.createBearbeitung(originalId, rendered, annotationen)),
  updateBearbeitung: async (fileId: number, rendered: Blob, annotationen: string): Promise<FileEntity> =>
    toFileEntity(await sandboxFiles.updateBearbeitung(fileId, rendered, annotationen)),
};

/** Read-only FileApi fuer die ScreenshotGallery freigegebener Findings. */
const sharedFileApi: FileApi = {
  list: async (params?: { schwachstelle_id?: number }): Promise<FileEntity[]> => {
    if (!params?.schwachstelle_id) return [];
    const rows: SandboxFile[] = (await api.get(`/restricted/shared/findings/${params.schwachstelle_id}/files`)).data;
    return rows.map((f) => toFileEntity(f, params.schwachstelle_id));
  },
  upload: async () => {
    throw new Error('Freigegebene Findings sind nur lesbar');
  },
  download: (id: number) => {
    window.open(`${API_BASE}/restricted/shared/files/${id}/download`, '_blank');
  },
  getPreviewUrl: (id: number) => `${API_BASE}/restricted/shared/files/${id}/preview`,
  delete: async () => {
    throw new Error('Freigegebene Findings sind nur lesbar');
  },
};

export const sandboxService = {
  me: async () => {
    const response = await api.get('/restricted/me');
    return response.data as { id: number; vorname: string; nachname: string; email: string; scope: 'full' | 'restricted' };
  },

  // ── Lookups ────────────────────────────────────────────────
  lookups: {
    kritikalitaeten: async (): Promise<string[]> => (await api.get('/restricted/lookups/kritikalitaeten')).data,
    schwachstellenklassen: async (): Promise<Schwachstellenklasse[]> =>
      (await api.get('/restricted/lookups/schwachstellenklassen')).data,
    tools: async (): Promise<Tool[]> => (await api.get('/restricted/lookups/tools')).data,
    risikoszenarien: async (): Promise<Risikoszenario[]> => (await api.get('/restricted/lookups/risikoszenarien')).data,
    cwe: async (q: string, limit = 30): Promise<CweEntry[]> =>
      (await api.get('/restricted/lookups/cwe', { params: { q, limit } })).data,
  },

  // ── Sandbox-Findings ───────────────────────────────────────
  list: async (includeArchived = false): Promise<SandboxFindingListItem[]> =>
    (await api.get('/restricted/findings', { params: { include_archived: includeArchived } })).data,

  get: async (id: number): Promise<SandboxFinding> => (await api.get(`/restricted/findings/${id}`)).data,

  create: async (data: SandboxFindingPayload): Promise<SandboxFinding> =>
    (await api.post('/restricted/findings', data)).data,

  update: async (id: number, data: Partial<SandboxFindingPayload>): Promise<SandboxFinding> =>
    (await api.put(`/restricted/findings/${id}`, data)).data,

  archive: async (id: number): Promise<SandboxFinding> => (await api.post(`/restricted/findings/${id}/archive`)).data,

  restore: async (id: number): Promise<SandboxFinding> => (await api.post(`/restricted/findings/${id}/restore`)).data,

  import: async (id: number, auditId: number): Promise<{ schwachstelle_id: number; audit_id: number; audit_name: string }> =>
    (await api.post(`/restricted/findings/${id}/import`, { audit_id: auditId })).data,

  importMany: async (
    findingIds: number[],
    auditId: number,
  ): Promise<{
    audit_id: number;
    audit_name: string;
    imported: { schwachstelle_id: number; audit_id: number; audit_name: string }[];
  }> => (await api.post('/restricted/findings/import', { finding_ids: findingIds, audit_id: auditId })).data,

  delete: async (id: number): Promise<void> => {
    await api.delete(`/restricted/findings/${id}`);
  },

  // ── Dateien eigener Sandbox-Findings ───────────────────────
  files: sandboxFiles,
  fileApi: sandboxFileApi,

  // ── Freigaben (nur Hauptaccount) ───────────────────────────
  freigaben: {
    list: async (): Promise<SandboxFreigabe[]> => (await api.get('/restricted/freigaben')).data,
    createForAudit: async (auditId: number): Promise<SandboxFreigabe> =>
      (await api.post('/restricted/freigaben', { audit_id: auditId })).data,
    createForFinding: async (schwachstelleId: number): Promise<SandboxFreigabe> =>
      (await api.post('/restricted/freigaben', { schwachstelle_id: schwachstelleId })).data,
    delete: async (id: number): Promise<void> => {
      await api.delete(`/restricted/freigaben/${id}`);
    },
  },

  // ── Freigegebene Inhalte (nur lesen) ───────────────────────
  shared: {
    audits: async (): Promise<SharedAuditListItem[]> => (await api.get('/restricted/shared/audits')).data,
    audit: async (auditId: number): Promise<SharedAuditDetail> =>
      (await api.get(`/restricted/shared/audits/${auditId}`)).data,
    findings: async (): Promise<SharedFindingListItem[]> => (await api.get('/restricted/shared/findings')).data,
    finding: async (id: number): Promise<SharedFinding> => (await api.get(`/restricted/shared/findings/${id}`)).data,
    previewUrl: (fileId: number) => `${API_BASE}/restricted/shared/files/${fileId}/preview`,
    downloadUrl: (fileId: number) => `${API_BASE}/restricted/shared/files/${fileId}/download`,
    fileApi: sharedFileApi,
  },

  // ── Doku ───────────────────────────────────────────────────
  docsMarkdown: async (): Promise<string> =>
    (await api.get('/restricted/docs.md', { responseType: 'text' })).data,
  docsUrl: `${API_BASE}/restricted/docs`,
  openapiUrl: `${API_BASE}/restricted/openapi.json`,
};
